import React, { useEffect, useState } from "react";
import { FileUp, Link2, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { VendorAvatar } from "@/components/Brand";
import { PageHead, Panel } from "@/pages/vendor/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const SOCIALS = ["instagram", "facebook", "linkedin", "tiktok", "pinterest", "youtube"];

function Assets() {
  const [assets, setAssets] = useState<any[]>([]);
  const [link, setLink] = useState({ title: "", url: "" });
  const [fileTitle, setFileTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/vendor/assets").then((r) => setAssets(r.data));
  useEffect(() => { load(); }, []);

  const addLink = async () => {
    try {
      await api.post("/vendor/assets/link", link);
      setLink({ title: "", url: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (file?: File) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("title", fileTitle || file.name);
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/vendor/assets/upload", fd);
      setFileTitle("");
      toast.success("Uploaded");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (id: string) => { await api.delete(`/vendor/assets/${id}`); load(); };

  return (
    <Panel className="space-y-4" data-testid="assets-panel">
      <h3 className="font-semibold">Brochures & links shared after each scan</h3>
      <div className="space-y-2">
        {assets.map((a) => (
          <div key={a.id} className="flex items-center gap-3 rounded-xl border border-border p-3" data-testid={`asset-row-${a.id}`}>
            {a.kind === "file" ? <FileUp className="h-4 w-4 text-emerald-600" /> : <Link2 className="h-4 w-4 text-emerald-600" />}
            <a href={a.kind === "file" ? fileUrl(a.file_path) : a.url} target="_blank" rel="noreferrer" className="flex-1 truncate text-sm font-medium hover:text-emerald-600">{a.title}</a>
            <Button size="icon" variant="ghost" onClick={() => remove(a.id)} data-testid={`asset-delete-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <div className="grid gap-2 sm:grid-cols-[1fr_1.4fr_auto]">
        <Input placeholder="Link title" value={link.title} onChange={(e) => setLink({ ...link, title: e.target.value })} data-testid="asset-link-title-input" />
        <Input placeholder="https://" value={link.url} onChange={(e) => setLink({ ...link, url: e.target.value })} data-testid="asset-link-url-input" />
        <Button variant="secondary" onClick={addLink} disabled={!link.title || !link.url} data-testid="asset-link-add-button"><Plus className="mr-1 h-4 w-4" /> Add link</Button>
      </div>
      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
        <Input placeholder="File title (e.g. 2026 Brochure)" value={fileTitle} onChange={(e) => setFileTitle(e.target.value)} data-testid="asset-file-title-input" />
        <label className="inline-flex cursor-pointer items-center justify-center rounded-md bg-secondary px-4 text-sm font-medium hover:bg-secondary/80">
          <Upload className="mr-1 h-4 w-4" /> {busy ? "Uploading..." : "Upload PDF / image"}
          <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="asset-file-input" />
        </label>
      </div>
    </Panel>
  );
}

function TypicalTypes({ value, onChange, disabled }: { value: string[]; onChange: (v: string[]) => void; disabled: boolean }) {
  const [types, setTypes] = useState<any[]>([]);
  useEffect(() => { api.get("/event-types").then((r) => setTypes(r.data)); }, []);
  return (
    <div className="space-y-2 rounded-xl border border-border p-3" data-testid="profile-typical-types">
      <p className="text-sm font-semibold">Event types I usually attend</p>
      <p className="text-xs text-muted-foreground">These appear first when you create an event or booth.</p>
      <div className="flex flex-wrap gap-2">
        {types.map((t) => {
          const on = value.includes(t.id);
          return (
            <button key={t.id} type="button" disabled={disabled} onClick={() => onChange(on ? value.filter((x) => x !== t.id) : [...value, t.id])}
              className={`rounded-sm border px-3 py-1.5 text-sm transition-colors ${on ? "border-primary bg-primary text-white" : "hover:border-primary"}`} data-testid={`profile-typical-type-${t.id}`}>
              {t.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Profile() {
  const { vendor, reload } = useAuth();
  const [p, setP] = useState<any>(vendor.vendor);
  const isOwner = vendor.user.staff_role === "owner";
  const set = (k: string, v: any) => setP({ ...p, [k]: v });

  const save = async () => {
    try {
      await api.put("/vendor/profile", p);
      await reload("vendor");
      toast.success("Profile saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const uploadLogo = async (file?: File) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/vendor/logo", fd);
      set("logo_path", data.logo_path);
      reload("vendor");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const text = (k: string, label: string) => (
    <div className="space-y-1.5"><Label>{label}</Label><Input value={p[k] || ""} onChange={(e) => set(k, e.target.value)} disabled={!isOwner} data-testid={`profile-${k}-input`} /></div>
  );

  return (
    <div>
      <PageHead eyebrow="What customers see" title="Profile & assets">
        {isOwner && <Button onClick={save} className="bg-primary text-white hover:bg-primary/90" data-testid="profile-save-button">Save profile</Button>}
      </PageHead>
      <div className="grid gap-4 xl:grid-cols-2">
        <Panel className="space-y-4">
          <div className="flex items-center gap-4">
            <VendorAvatar vendor={p} size={64} />
            {isOwner && (
              <label className="cursor-pointer text-sm font-semibold text-emerald-600 hover:text-emerald-700">
                Upload logo <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="profile-logo-input" />
              </label>
            )}
          </div>
          {text("name", "Company name")}
          {text("tagline", "Tagline (one line)")}
          <div className="space-y-1.5"><Label>Description</Label><Textarea value={p.description || ""} onChange={(e) => set("description", e.target.value)} disabled={!isOwner} data-testid="profile-description-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            {text("category", "Category")}
            <div className="space-y-1.5"><Label>Brand colour</Label><Input type="color" value={p.brand_color} onChange={(e) => set("brand_color", e.target.value)} disabled={!isOwner} className="h-10 p-1" data-testid="profile-color-input" /></div>
          </div>
          {text("website", "Website")}
          {text("booking_url", "Booking link (Calendly, etc.)")}
          <TypicalTypes value={p.typical_event_types || []} onChange={(v) => set("typical_event_types", v)} disabled={!isOwner} />
          <div className="space-y-3 rounded-xl border border-border p-3">
            <label className="flex items-center justify-between text-sm">In-app chat with customers <Switch checked={p.chat_enabled} onCheckedChange={(v) => set("chat_enabled", v)} disabled={!isOwner} data-testid="profile-chat-switch" /></label>
            <label className="flex items-center justify-between text-sm">Email me on every new lead <Switch checked={p.notify_new_lead} onCheckedChange={(v) => set("notify_new_lead", v)} disabled={!isOwner} data-testid="profile-notify-switch" /></label>
          </div>
        </Panel>
        <div className="space-y-4">
          <Panel className="space-y-3">
            <h3 className="font-semibold">Social links</h3>
            <div className="grid gap-2 sm:grid-cols-2">
              {SOCIALS.map((s) => (
                <Input key={s} placeholder={`${s} URL`} value={p.socials?.[s] || ""} disabled={!isOwner}
                  onChange={(e) => set("socials", { ...p.socials, [s]: e.target.value })} data-testid={`profile-social-${s}-input`} />
              ))}
            </div>
            <h3 className="pt-2 font-semibold">Call-to-action buttons</h3>
            {(p.ctas || []).map((c: any, i: number) => (
              <div key={i} className="grid grid-cols-[1fr_1.4fr_auto] gap-2">
                <Input value={c.label} placeholder="Label" disabled={!isOwner} onChange={(e) => set("ctas", p.ctas.map((x: any, j: number) => (j === i ? { ...x, label: e.target.value } : x)))} data-testid={`profile-cta-label-${i}`} />
                <Input value={c.url} placeholder="https://" disabled={!isOwner} onChange={(e) => set("ctas", p.ctas.map((x: any, j: number) => (j === i ? { ...x, url: e.target.value } : x)))} data-testid={`profile-cta-url-${i}`} />
                <Button size="icon" variant="ghost" disabled={!isOwner} onClick={() => set("ctas", p.ctas.filter((_: any, j: number) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            {isOwner && <Button size="sm" variant="secondary" onClick={() => set("ctas", [...(p.ctas || []), { label: "", url: "" }])} data-testid="profile-add-cta-button"><Plus className="mr-1 h-4 w-4" /> Add CTA</Button>}
          </Panel>
          <Assets />
        </div>
      </div>
    </div>
  );
}
